<template>
  <v-card class="d-flex flex-column" style="height: 100vh">
    <v-card-title class="primary white--text d-flex align-center flex-shrink-0">
      <v-icon dark left>mdi-ticket-confirmation</v-icon>
      Party Check-in & Bändel
      <v-spacer></v-spacer>
      <v-btn icon dark @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-0" style="overflow-y: auto; flex-grow: 1">
      <v-container fluid class="pa-4 d-flex flex-column h-100">
        <div v-if="!isListViewVisible" class="flex-grow-1 d-flex flex-column justify-center align-center text-center">
          <v-icon size="100" color="primary lighten-2">mdi-party-popper</v-icon>

          <div class="grey--text text--darken-2 mt-4" style="font-size: 1.25rem; font-weight: 500">Ausgegebene Bändel</div>

          <div class="display-3 font-weight-bold primary--text" style="line-height: 1.1; font-size: 6rem !important">
            <v-progress-circular v-if="loading" indeterminate color="primary"></v-progress-circular>
            <span v-else
              >{{ totalWristbandsGiven }} <span style="font-size: 3rem; color: #999">/ {{ totalExpectedGuests }}</span></span
            >
          </div>

          <p class="body-2 grey--text text--darken-2 mt-6 px-4" style="max-width: 500px">
            Bisher sind {{ totalSignups }} Hauptgäste angemeldet (plus {{ totalPlusOnes }} Begleitungen).
          </p>

          <v-btn color="primary" @click="isListViewVisible = true" x-large rounded elevation="2" class="mt-6" :disabled="loading">
            <v-icon left>mdi-format-list-bulleted</v-icon>
            Check-in starten
          </v-btn>
        </div>

        <div v-else class="d-flex flex-column h-100">
          <v-btn text @click="isListViewVisible = false" class="mb-2 align-self-start">
            <v-icon left>mdi-chevron-left</v-icon>
            Zurück zur Übersicht
          </v-btn>

          <v-row class="mb-2" align="center">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="searchQuery"
                label="Gast suchen (Name, Begleitung, Studio, Firma...)"
                prepend-inner-icon="mdi-magnify"
                dense
                hide-details
                clearable
                outlined
                rounded
                color="primary"></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-chip-group v-model="activeFilter" mandatory active-class="primary--text">
                <v-chip value="all">Alle</v-chip>
                <v-chip value="pending">Bändel offen</v-chip>
                <v-chip value="employees">Mitarbeiter</v-chip>
                <v-chip value="externals">Externe / Firma</v-chip>
              </v-chip-group>
            </v-col>
          </v-row>

          <v-data-table
            :item-class="getRowClass"
            :headers="tableHeaders"
            :items="filteredGuests"
            :items-per-page="15"
            class="elevation-1 rounded-lg flex-grow-1"
            :loading="loading"
            loading-text="Lade Gästeliste..."
            no-data-text="Keine Anmeldungen gefunden."
            no-results-text="Keine Treffer für diese Suche.">
            <template v-slot:item.guest="{ item }">
              <div class="d-flex align-center py-2">
                <v-avatar :color="item.employee ? 'primary' : 'grey darken-3'" size="40" class="white--text mr-3 font-weight-bold">
                  {{ getInitials(item.fullName) }}
                </v-avatar>
                <div>
                  <div class="font-weight-bold text-subtitle-1 leading-tight">{{ item.fullName }}</div>
                  <div class="text-caption grey--text">{{ item.email }}</div>
                </div>
              </div>
            </template>

            <template v-slot:item.origin="{ item }">
              <div v-if="item.employee">
                <v-chip x-small color="primary" outlined class="mb-1">Mitarbeiter</v-chip>
                <div class="text-caption font-weight-medium">{{ item.employeeStudio || "Zentrale/Unbekannt" }}</div>
              </div>
              <div v-else-if="item.company">
                <v-chip x-small color="orange" outlined class="mb-1">Firma</v-chip>
                <div class="text-caption font-weight-medium">{{ item.company }}</div>
              </div>
              <div v-else>
                <v-chip x-small color="grey" outlined class="mb-1">Mitglied</v-chip>
                <div class="text-caption">{{ item.memberStudio || "Kein Studio hinterlegt" }}</div>
              </div>
            </template>

            <template v-slot:item.plusOne="{ item }">
              <div v-if="item.hasPlusOne">
                <v-icon small color="green" class="mr-1">mdi-account-plus</v-icon>
                <span class="font-weight-medium">{{ item.plusOne }}</span>
              </div>
              <div v-else class="text-caption grey--text">- Keine -</div>
            </template>

            <template v-slot:item.wristbands="{ item }">
              <div class="d-flex align-center">
                <v-chip
                  :color="item.wristbands === item.maxWristbands ? 'success' : item.wristbands > 0 ? 'warning' : 'grey'"
                  dark
                  small
                  class="mr-4 font-weight-bold">
                  {{ item.wristbands }} / {{ item.maxWristbands }}
                </v-chip>

                <v-btn-toggle v-model="item.wristbands" mandatory dense @change="setWristbands(item, $event)">
                  <v-btn :value="0" small icon :disabled="handlingId === item._id">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                  <v-btn :value="1" small color="primary" outlined :disabled="handlingId === item._id"> 1 </v-btn>
                  <v-btn v-if="item.maxWristbands === 2" :value="2" small color="primary" outlined :disabled="handlingId === item._id"> 2 </v-btn>
                </v-btn-toggle>

                <v-progress-circular
                  v-if="handlingId === item._id"
                  indeterminate
                  size="20"
                  width="2"
                  color="primary"
                  class="ml-2"></v-progress-circular>
              </div>
            </template>
          </v-data-table>
        </div>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import api from "@/api"

export default {
  name: "PartyCheckin",
  data() {
    return {
      isListViewVisible: false,
      loading: true,
      guests: [],
      searchQuery: "",
      activeFilter: "all",
      handlingId: null, // Verhindert Spam-Klicks während dem Speichern

      tableHeaders: [
        { text: "Gast", value: "guest", sortable: true, width: "30%" },
        { text: "Herkunft", value: "origin", sortable: false, width: "20%" },
        { text: "Begleitung", value: "plusOne", sortable: true, width: "20%" },
        { text: "Bändel Ausgabe", value: "wristbands", sortable: false, width: "30%" },
      ],
    }
  },

  computed: {
    totalSignups() {
      return this.guests.length
    },
    totalPlusOnes() {
      return this.guests.filter((g) => g.hasPlusOne).length
    },
    totalExpectedGuests() {
      return this.totalSignups + this.totalPlusOnes
    },
    totalWristbandsGiven() {
      return this.guests.reduce((sum, guest) => sum + (guest.wristbands || 0), 0)
    },

    filteredGuests() {
      let result = this.guests

      // 1. Filter Buttons
      if (this.activeFilter === "pending") {
        result = result.filter((g) => g.wristbands < g.maxWristbands)
      } else if (this.activeFilter === "employees") {
        result = result.filter((g) => g.employee)
      } else if (this.activeFilter === "externals") {
        result = result.filter((g) => g.company && g.company.trim() !== "")
      }

      // 2. Search Text
      if (this.searchQuery && this.searchQuery.trim() !== "") {
        const search = this.searchQuery.toLowerCase().trim()

        result = result.filter((guest) => {
          const name = (guest.fullName || "").toLowerCase()
          const email = (guest.email || "").toLowerCase()
          const plusOne = (guest.plusOne || "").toLowerCase()
          const company = (guest.company || "").toLowerCase()
          const empStudio = (guest.employeeStudio || "").toLowerCase()
          const memStudio = (guest.memberStudio || "").toLowerCase()

          return (
            name.includes(search) ||
            email.includes(search) ||
            plusOne.includes(search) ||
            company.includes(search) ||
            empStudio.includes(search) ||
            memStudio.includes(search)
          )
        })
      }

      // 3. Sortierung (Komplett abgeholte Bändel nach unten, Rest alphabetisch)
      result.sort((a, b) => {
        // NEU: Hier greifen wir auf den eingefrorenen Status zu!
        const aComplete = a._initialComplete
        const bComplete = b._initialComplete

        // Wenn a fertig ist, aber b nicht -> a nach unten
        if (aComplete && !bComplete) return 1
        // Wenn b fertig ist, aber a nicht -> b nach unten
        if (!aComplete && bComplete) return -1

        // Beide im gleichen Status -> Alphabetisch nach Name sortieren
        return (a.fullName || "").localeCompare(b.fullName || "")
      })

      // Erst GANZ AM SCHLUSS wird zurückgegeben
      return result
    },
  },

  created() {
    this.loadCheckinData()
  },

  methods: {
    getRowClass(item) {
      return item.wristbands === item.maxWristbands ? "completed-row" : ""
    },
    async loadCheckinData() {
      this.loading = true
      try {
        const data = await api.fetchPartyCheckinList()

        // NEU: Wir speichern den Status beim Laden ab, damit die Reihenfolge fixiert bleibt
        this.guests = data.map((guest) => ({
          ...guest,
          _initialComplete: guest.wristbands === guest.maxWristbands,
        }))
      } catch (e) {
        console.error("Fehler beim Laden der Check-in Liste:", e)
        this.$toast.error("Gästeliste konnte nicht geladen werden.")
      } finally {
        this.loading = false
      }
    },

    async setWristbands(guest, count) {
      this.handlingId = guest._id
      try {
        const result = await api.updateWristbands(guest._id, count)
        // Lokales Update zur Sicherheit mit dem DB-Wert überschreiben
        guest.wristbands = result.wristbands

        if (count > 0) {
          this.$toast.success(`${count} Bändel an ${guest.firstName} vergeben.`)
        } else {
          this.$toast.info(`Bändel für ${guest.firstName} zurückgesetzt.`)
        }
      } catch (e) {
        console.error("Fehler beim Bändel-Update:", e)
        this.$toast.error("Speichern fehlgeschlagen. Bitte neu laden.")
        // Rollback im UI bei Fehler
        await this.loadCheckinData()
      } finally {
        this.handlingId = null
      }
    },

    getInitials(name) {
      if (!name) return "?"
      const parts = name.trim().split(" ")
      const firstInitial = parts[0] ? parts[0][0] : ""
      const lastInitial = parts.length > 1 ? parts[parts.length - 1][0] : ""

      let initials = firstInitial
      if (lastInitial && lastInitial !== firstInitial) {
        initials += lastInitial
      }
      return initials.toUpperCase()
    },
  },
}
</script>

<style scoped>
::v-deep .completed-row {
  opacity: 0.5;
  transition: opacity 0.3s ease-in-out;
}
.leading-tight {
  line-height: 1.2;
}

/* Fix für den Toggle-Button, damit er in der Table schmaler bleibt */
::v-deep .v-btn-toggle--dense .v-btn {
  height: 32px !important;
  min-width: 40px !important;
  padding: 0 8px !important;
}

.h-100 {
  height: 100%;
}
</style>
